import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaYoutube, FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';
import { AppContext } from '../../context/AppContext';
import { motion } from 'framer-motion';
import '../../styles/footer.css';

const ModernFooter = () => {
  const { t } = useTranslation();
  const { language, theme, isRTL } = useContext(AppContext);
  
  const currentYear = new Date().getFullYear();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }
  };

  return (
    <footer className={`footer relative overflow-hidden backdrop-blur-md bg-white text-gray-900 ${isRTL ? 'font-arabic' : 'font-sans'}`} style={{ backgroundColor: '#ffffff' }}>
      {/* Accent bar at top - matching navbar style */}
      <div className="accent-bar"></div>
      
      <div className="container mx-auto py-12 px-6">
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
        >
       
          {/* Quick Links Column */}
          <motion.div variants={itemVariants} >
            <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{t('footer.quickLinks')}</h3>
            <ul className={`space-y-2 ${isRTL ? 'text-right' : 'text-left'}`}>  
              {[
                { to: "/", text: t('nav.home') },
                { to: "/about", text: t('nav.about') },
                { to: "/pedagogy", text: t('nav.pedagogy') },
                { to: "/events", text: t('nav.events') },
                { to: "/registration", text: t('nav.registration') },
                { to: "/contact", text: t('nav.contact') }
              ].map((link, index) => (
                <motion.li key={index} whileHover={{ x: isRTL ? -5 : 5 }}>
                  <Link 
                    to={link.to} 
                    className={`text-sm ${theme === 'dark' ? 'text-gray-300 hover:text-accent' : 'text-gray-700 hover:text-primary'} transition-colors duration-300`}
                  >
                    {link.text}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Information Column */}
          <motion.div variants={itemVariants}>
            <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{t('footer.contactUs')}</h3>
            <ul className={`space-y-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>  
              <li className="flex items-start">
                <FaMapMarkerAlt className="mr-2 text-accent mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold mb-1">
                    {language === 'ar' ? 'سوسة:' : 'Sousse:'}
                  </p>
                  <p className="text-sm">
                    {language === 'ar'
                      ? 'مبنى حبيبة، شارع جوهرة الساحل - الخزامى - سوسة'
                      : 'Immeuble Habiba, Rue Jawharat Essahel - Khezama - Sousse'}
                  </p>
                </div>
              </li>
              <li className="flex items-center">
                <FaPhone className="mr-2 text-accent flex-shrink-0" />
                <span className="text-sm">
                  {t('footer.phone')}: 56069069
                </span>
              </li>
              <li className="flex items-start mt-2">
                <FaMapMarkerAlt className="mr-2 text-accent mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold mb-1">
                    {language === 'ar' ? 'القيروان:' : 'Kairouan:'}
                  </p>
                  <p className="text-sm">
                    {language === 'ar'
                      ? 'شارع طارق بن زياد - القيروان'
                      : 'Rue Tarek Ibn Zied - Kairouan'}
                  </p>
                </div>
              </li>
              <li className="flex items-center">
                <FaPhone className="mr-2 text-accent flex-shrink-0" />
                <span className="text-sm">
                  {t('footer.phone')}: 51022929
                </span>
              </li>
              <li className="flex items-center mt-2">
                <FaEnvelope className="mr-2 text-accent flex-shrink-0" />
                <span className="text-sm">dispositifnouvellechance@gmail.com</span>
              </li>
            </ul>
          </motion.div>
             {/* Logo and Social Media Column */}
             <motion.div variants={itemVariants} className="flex flex-col items-center">
            <Link to="/" className="flex items-center mb-4">
              <img 
                src={language === 'fr' ? '/image/logofr.png' : '/image/logoar.png'} 
                alt="DNC Logo" 
                className="h-32 w-auto"
              />
            </Link>
            <div className="partners-logo flex justify-center mt-4">
              <img 
                src="/image/apropos/Barre logo partenaires.png" 
                alt={language === 'ar' ? 'شركاء برنامج الفرصة الجديدة' : 'Partenaires du Dispositif Nouvelle Chance'} 
                className="partners-banner"
              />
            </div>
            <div className="flex space-x-4 rtl:space-x-reverse mt-4">
              {[
                { icon: <FaFacebook size={20} />, url: "https://www.facebook.com/DispositifNationalNouvelleChance" },
                { icon: <FaInstagram size={20} />, url: "https://www.instagram.com/dnc.tn" },
                { icon: <FaLinkedin size={20} />, url: "https://www.linkedin.com/company/iecd-tunisie" }
              ].map((social, index) => (
                <motion.a 
                  key={index}
                  href={social.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`social-icon ${theme === 'dark' ? 'text-gray-300 hover:text-accent' : 'text-gray-700 hover:text-primary'}`}
                  whileHover={{ y: -3, scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </motion.div>

        </motion.div>

        {/* Copyright */}
        <motion.div 
          className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} mt-8 pt-6 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          viewport={{ once: false }}
        >
          <p className="text-sm">&copy; {currentYear} Dispositif Nouvelle Chance. {t('footer.rights')}</p>
        </motion.div>
      </div>
    </footer>
  );
};

export default ModernFooter;
